import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Box,useTheme,useMediaQuery, Divider } from "@mui/material";
import { SectionHeader } from "../../SectionHeader";
import idIcon from "../../../../assets/svgIcons/id.svg";
import SummarySectionHeader from "../../../../components/applicantDetailsTabs/SummarySectionHeader";

const ApplicantAdditionalDetails = ({
  fields,
  renderField,
  formikRef,
  values,
  nationalityOptions = [],
  birthCityOptions = [],
  dropdownData = {},
  pageType="",
  isDIYJourney = false
}: any) => {
  const formValues = values || formikRef?.current?.values || {};
  
  // Several field handlers in this step intentionally call setFieldValue
  // with shouldValidate=false ("prevent validation on each change"), so
  // Formik's `errors` can lag behind the latest `values` until the user
  // blurs a field (which forces a full validateForm via validateOnBlur).
  // The Continue button's completeness check reads `errors` directly, so
  // without this it stays disabled until that unrelated blur happens.
  // Re-validate as soon as this step's values change so `errors` stays
  // in sync and Continue enables the moment the last field is filled.
  useEffect(() => {
    formikRef?.current?.validateForm();
  }, [formValues]);

  useEffect(() => {
    if (nationalityOptions.length > 0) {
      // Set default nationality to 'Indian' if not already set
      const nationalityField = fields.find(
        (f: any) => /nationality/i.test(f?.name || "") || /naionality/i.test(f?.name || ""),
      );
      if (nationalityField) {
        const fieldName = nationalityField.name;
        const currentValue = formikRef?.current?.values?.[fieldName];
        if (
          !currentValue ||
          (typeof currentValue === "object" &&
            !currentValue.value &&
            !currentValue.label)
        ) {
          const indiaOption = nationalityOptions.find(
            (opt:any) =>
              opt.label?.toLowerCase() === "indian" ||
              opt.value?.toLowerCase() === "indian" ||
              opt.value?.toLowerCase() === "in",
          );
          if (indiaOption) {
            formikRef?.current?.setFieldValue(fieldName, indiaOption, false);
          }
        }
      }
    }
  }, [fields, formikRef, nationalityOptions]);

  const enrichField = (field: any) => {
    const name = String(field?.name || "");
    const label = String(field?.label || "");
    if (name === "applicantDependents") {
      return { ...field, labelHint: "" };
    }
    if (name === "loanRequestedRoi") {
      return { ...field, labelHint: "(Requested ROI cannot be more than 20%)" };
    }
    if (/nationality/i.test(name) || /naionality/i.test(name)) {
      return { ...field, options: { options: nationalityOptions } };
    }
    if (
      /city.*birth|birth.*city/i.test(name) ||
      /city.*birth|birth.*city/i.test(label) ||
      name.toLowerCase() === "applicantcitybirth"
    ) {
      return {
        ...field,
        options: {
          options:
            birthCityOptions.length > 0
              ? birthCityOptions
              : field?.options?.options || [],
        },
      };
    }
    return field;
  };

  useEffect(() => {
    const sourceLoanAmount = formikRef?.current?.values?.loanAmount;
    const targetLoanAmount = formikRef?.current?.values?.loanLoanAmount;

    if (
      sourceLoanAmount !== undefined &&
      sourceLoanAmount !== null &&
      String(sourceLoanAmount).trim() !== "" &&
      (targetLoanAmount === undefined ||
        targetLoanAmount === null ||
        String(targetLoanAmount).trim() === "")
    ) {
      formikRef?.current?.setFieldValue("loanLoanAmount", sourceLoanAmount, false);
    }
  }, [formikRef?.current?.values?.loanAmount]);

  // DIY journey: Requested Tenure / Requested ROI are hidden and hardcoded
  // by the backend (120 / 13). Continue's "required fields filled" check
  // still looks at these fields on the underlying `fields` config, so seed
  // them into Formik state even though no input is rendered for them.
  useEffect(() => {
    if (!isDIYJourney) return;
    const tenureValue = formValues?.loanLoanTenure;
    if (
      tenureValue === undefined ||
      tenureValue === null ||
      String(tenureValue).trim() === ""
    ) {
      formikRef?.current?.setFieldValue("loanLoanTenure", 120, false);
    }
    const roiValue = formValues?.loanRequestedRoi;
    if (
      roiValue === undefined ||
      roiValue === null ||
      String(roiValue).trim() === ""
    ) {
      formikRef?.current?.setFieldValue("loanRequestedRoi", 13, false);
    }
  }, [isDIYJourney, formValues?.loanLoanTenure, formValues?.loanRequestedRoi]);

  const sortedFields = useMemo(() => {
    if (!Array.isArray(fields)) return [];
    return [...fields].sort(
      (a: any, b: any) => (a?.fieldOrder ?? 0) - (b?.fieldOrder ?? 0),
    );
  }, [fields]);

  const applicantFields = useMemo(
    () =>
      sortedFields.filter((f: any) =>
        String(f?.name || "")
          .toLowerCase()
          .startsWith("applicant"),
      ),
    [sortedFields],
  );

  const loanFields = useMemo(
    () =>
      sortedFields.filter((f: any) => {
        const name = String(f?.name || "").toLowerCase();
        if (!name.startsWith("loan")) return false;
        // Requested Tenure and Requested ROI are hardcoded by the backend
        // (120 / 13) for the DIY journey, so hide them from the form.
        if (isDIYJourney && (name === "loanloantenure" || name === "loanrequestedroi")) {
          return false;
        }
        return true;
      }),
    [sortedFields, isDIYJourney],
  );

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const requiredFields = sectionFields.filter(
      (f: any) => f?.required !== false,
    );
    const filledCount = requiredFields.filter((field: any) =>
      isFilled(formValues?.[field?.name]),
    ).length;
    return `${filledCount} / ${requiredFields.length}`;
  };
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm")); 
  return (
    <>
      <Grid container mt={1} ml={isXsOrSm?"2px":"0"} spacing={2} sx={{ px: { xs: 0, sm: 2 }, mb: 2 }}>
        
        {pageType==="FORM" && 
          <Grid item xs={12}>
            <Box sx={{ mb: 2, mt: 2 }}>
                <SectionHeader
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#e5feffff",
                      }}
                    >
                      <img
                        src={idIcon}
                        alt="icon"
                        style={{
                          width: 28,
                          height: 28,
                        }}
                      />
                    </Box>
                  }
                  title={"Applicant Details"}
                  subtitle={"Applicant Related Details"}
                  completion={getCompletion(applicantFields)}
                />
            </Box>
          </Grid>
        }
        {
          pageType==="SUMMARY" && 
          <Grid item xs={12}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <SummarySectionHeader
                title={"Applicant Details"}
              />
            </Box>
          </Grid>
        }
        {applicantFields.map((field: any) => (
          <React.Fragment key={field?.id || field?.name}>
            {renderField?.(enrichField(field))}
          </React.Fragment>
        ))}
        {pageType==="FORM" && 
          <Grid item xs={12}>
            {isXsOrSm && <Divider />}
            <Box sx={{ mb: 2, mt: 2 }}>
              <SectionHeader
                icon={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      bgcolor: "#e5feffff",
                    }}
                  >
                    <img
                      src={idIcon}
                      alt="icon"
                      style={{
                        width: 28,
                        height: 28,
                      }}
                    />
                  </Box>
                }
                title={"Loan Details"}
                subtitle={"Loan Related Details"}
                completion={getCompletion(loanFields)}
              />
            </Box>
          </Grid>
        }
        {
          pageType==="SUMMARY" && 
          <Grid item xs={12}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <SummarySectionHeader
                title={"Loan Details"}
              />
            </Box>
          </Grid>
        }
        {loanFields.map((field: any) => (
          <React.Fragment key={field?.id || field?.name}>
            {renderField?.(enrichField(field))}
          </React.Fragment>
        ))}
      </Grid>
    </>
  );
};

export default ApplicantAdditionalDetails;